/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { useLocation } from "react-router";
import Typography from "@mui/material/Typography";
import { mergeSx } from "@/src/utils/mergeSx.ts";
import { Box, Stack } from "@mui/material";
import { useId } from "react";
import { NavigationItemCategory } from "@/src/types/Navigation.ts";
import NavCategorySelectFlyout from "@/src/components/Header/NavCategorySelectFlyout/NavCategorySelectFlyout.tsx";
import { useHeader } from "@/src/components/Header/HeaderProvider.tsx";
import { navItemCategoryStyle } from "@/src/components/Header/NavItemCategory/navItemCategoryStyle.ts";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import NavCategoryAccordion from "@/src/components/Header/NavCategoryAccordion/NavCategoryAccordion.tsx";
import { createPortal } from "react-dom";

export default function NavItemCategory({ label, items, isMobileNav }: NavigationItemCategory) {
  const theme = useTheme();
  const { activeSection, setActiveSection, mobileNavActive, headerMobileCategorySelectSlot } = useHeader();
  const { pathname } = useLocation();
  const id = useId();

  const itemsWithActiveState = items.map((item) => ({ ...item, active: pathname === item.to }));
  const isActive = activeSection === id;
  const isSubCategoryActive = itemsWithActiveState.some((item) => item.active);

  const isOpen = isActive || isSubCategoryActive;

  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const NavCategorySelectFlyoutEle = (
    <NavCategorySelectFlyout id={id} items={itemsWithActiveState} active={!isDesktop ? !mobileNavActive && isSubCategoryActive : isOpen} />
  );

  const mobileAccordionContentId = id + "mobileAccordion";
  const ariaControls = isMobileNav ? mobileAccordionContentId : id;

  return (
    <Box onMouseLeave={() => isDesktop && setActiveSection?.(null)} onMouseEnter={() => isDesktop && setActiveSection?.(id)} sx={{ width: "100%" }}>
      <Stack
        direction={"row"}
        component={"button"}
        sx={mergeSx(
          (theme) => ({
            color: theme.palette.text.primary,
          }),
          navItemCategoryStyle.navItemCategory,
          !isOpen && navItemCategoryStyle.unactive,
          isSubCategoryActive && navItemCategoryStyle.active,
        )}
        aria-controls={ariaControls}
        aria-expanded={isActive}
        onClick={() => setActiveSection?.((prev) => (prev ? null : id))}
      >
        <Typography variant={isDesktop ? "button" : "h5"} sx={{ margin: 0 }}>
          {label}
        </Typography>
        <Box sx={{ display: { xs: "block", lg: "none" } }}>{isActive ? <ExpandLess fontSize={"medium"} /> : <ExpandMore fontSize={"medium"} />}</Box>
      </Stack>
      <Box sx={{ display: { xs: "flex", lg: "none" } }} id={mobileAccordionContentId}>
        <NavCategoryAccordion items={itemsWithActiveState} open={isActive} />
      </Box>
      {isMobileNav
        ? headerMobileCategorySelectSlot && !mobileNavActive && createPortal(NavCategorySelectFlyoutEle, headerMobileCategorySelectSlot)
        : NavCategorySelectFlyoutEle}
    </Box>
  );
}
